import { Link } from '@chakra-ui/next-js';
import {
  Alert,
  Box,
  Checkbox,
  Divider,
  Heading,
  List,
  ListItem,
  OrderedList,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  UnorderedList,
} from '@chakra-ui/react';
import { type MDXRemoteProps } from 'next-mdx-remote';
import Image from 'next/image';
import { ReactNode } from 'react';
import { generateImageSizeMediaQueries } from '../../../helpers';
import getYoutubeVideoId from '../lib/getYoutubeVideoId';

const sizes = generateImageSizeMediaQueries({
  base: 'calc((100vw - 2rem))',
  sm: 'calc((100vw - 4rem))',
  md: '48rem',
});

const components: MDXRemoteProps['components'] = {
  img: ({ alt, src }) => (
    <Image
      alt={alt || ''}
      height={1}
      sizes={sizes}
      src={src || ''}
      style={{ objectFit: 'fill', borderRadius: '.5rem', width: '100%' }}
      width={800}
    />
  ),
  h1: ({ children }) => (
    <Heading
      alignSelf='center'
      as='h1'
      size='2xl'
    >
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading
      as='h2'
      color='gray.base'
      size={{ base: 'lg', md: 'xl' }}
      textAlign='initial'
    >
      {children}
    </Heading>
  ),
  h3: ({ children }) => (
    <Heading
      as='h3'
      color='gray.base'
      size='lg'
    >
      {children}
    </Heading>
  ),
  h4: ({ children }) => (
    <Heading
      as='h4'
      size='md'
    >
      {children}
    </Heading>
  ),
  h5: ({ children }) => (
    <Heading
      as='h5'
      size='sm'
    >
      {children}
    </Heading>
  ),
  h6: ({ children }) => (
    <Heading
      as='h6'
      size='xs'
    >
      {children}
    </Heading>
  ),
  p: ({ children }) => (
    <Text
      whiteSpace='pre-wrap'
      width='full'
    >
      {children}
    </Text>
  ),
  em: ({ children }) => <Text as='em'>{children}</Text>,
  ul: ({ children }) => {
    const includeChildrenCheckbox =
      Array.isArray(children) &&
      children.some(
        (child) =>
          child &&
          typeof child === 'object' &&
          Array.isArray(child.props?.children) &&
          child.props.children.some(
            (el: any) =>
              el && typeof el === 'object' && el.type === 'input' && el?.props?.type === 'checkbox',
          ),
      );
    const Component = includeChildrenCheckbox ? List : UnorderedList;

    return (
      <Component
        mx='0'
        pl={includeChildrenCheckbox ? 'md' : '2xl'}
      >
        {children}
      </Component>
    );
  },
  ol: ({ children }) => (
    <OrderedList
      m='0'
      pl='2xl'
    >
      {children}
    </OrderedList>
  ),
  li: ({ children }) => {
    const includeChildrenCheckbox =
      Array.isArray(children) &&
      children.some(
        (child) =>
          child &&
          typeof child === 'object' &&
          child.type === 'input' &&
          child?.props?.type === 'checkbox',
      );

    const preparedChildren = includeChildrenCheckbox
      ? children.flatMap<ReactNode>((el, index) => {
          const isCheckbox =
            el && typeof el === 'object' && el.type === 'input' && el.props?.type === 'checkbox';

          if (isCheckbox) {
            return [
              <Checkbox
                key={index}
                colorScheme='primary'
                defaultChecked={!!el?.props?.checked}
                disabled
                mr='xs'
                mt='3xs'
              />,
            ];
          }

          const isLabel = el && typeof el === 'string' && !!el.trim();

          if (isLabel) {
            return [el];
          }

          return [];
        })
      : children;

    return <ListItem m='0'>{preparedChildren}</ListItem>;
  },
  a: ({ href, children }) => (
    <Link
      color='primary.base'
      href={href!}
    >
      {children}
    </Link>
  ),
  blockquote: ({ children }) => (
    <Alert
      as='blockquote'
      borderRadius='4px'
      colorScheme='gray'
      variant='left-accent'
    >
      {children}
    </Alert>
  ),
  hr: () => <Divider />,
  pre: ({ children }) => (
    <Box
      as='pre'
      bgColor='gray.base'
      borderRadius='md'
      color='white'
      px='md'
      py='3xs'
      width='full'
    >
      {children}
    </Box>
  ),
  table: ({ children }) => (
    <TableContainer
      border='1px'
      borderColor='gray.medium'
      borderRadius='xl'
      minW='full'
    >
      <Table
        size='sm'
        variant='simple'
      >
        {children}
      </Table>
    </TableContainer>
  ),
  thead: ({ children }) => <Thead>{children}</Thead>,
  tbody: ({ children }) => <Tbody>{children}</Tbody>,
  tfoot: ({ children }) => <Tfoot>{children}</Tfoot>,
  tr: ({ children }) => <Tr>{children}</Tr>,
  th: ({ children }) => <Th>{children}</Th>,
  td: ({ children }) => <Td>{children}</Td>,
  strong: ({ children }) => (
    <Text
      as='span'
      fontWeight='semibold'
    >
      {children}
    </Text>
  ),
  YouTube: ({ link }: { link: string }) => {
    const id = getYoutubeVideoId(link);
    if (!id) return null;

    return (
      <Box
        borderRadius='lg'
        height='0'
        maxW='3xl'
        overflow='hidden'
        pb={{ base: '56.25%', md: '27rem' }}
        position='relative'
        sx={{
          '.youtube_player': {
            position: 'absolute',
            top: 0,
            left: 0,
            width: 'full',
            height: 'full',
            border: 0,
          },
        }}
        width='full'
      >
        <iframe
          allow='autoplay; encrypted-media'
          className='youtube_player'
          src={`https://www.youtube.com/embed/${id}`}
          title='Embedded YouTube video'
        />
      </Box>
    );
  },
};

export default components;
